import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSocialShareAccessDeniedStep from 'containers/SocialShareModal/SocialShareAccessDeniedStep';
import { useSocialShareModal } from 'containers/SocialShareModal/SocialShareModalContext';
import useOnShare from 'containers/SocialShareModal/useOnShare';
import useTikTokAuthenticator from 'hooks/useTikTokAuthenticator';
import { scheduleTikTokVideo, shareTikTokVideo } from 'redux/modules/download';
import { showError } from 'redux/modules/notification';
import { getTikTokCreatorInfo, tiktokSelector } from 'redux/modules/social';
import { Dispatch } from 'redux/types';
import { SocialSharePlatformData } from '../types';
import { TITKOK_ACCESS_DENIED_MESSAGE } from './constants';
import {
  useTikTokAuthorizationStep,
  useTikTokComposerStep,
  useTikTokDisclosureStep,
} from './steps';
import useTikTokComposerValues from './steps/useTikTokComposerValues';
import { TikTokSocialShareStep } from './types';

export default function useTikTokSocialShare(): SocialSharePlatformData<
  TikTokSocialShareStep
> {
  const {
    providerUserId,
    profileImageUrl,
    username,
    creatorInfo,
    postStatus,
  } = useSelector(tiktokSelector);

  const dispatch = useDispatch<Dispatch>();

  const {
    description,
    onAuthSuccess,
    onAuthError,
    goToStep,
  } = useSocialShareModal();

  const {
    isDisclosureVisible,
    brandedContentSelected,
    privacyLevel,
    userAllowance,
    brandContent,
    brandOrganic,
    onVisibilityChange,
    onPrivacyLevelChange,
    onUserAllowanceChange,
    onBrandContentChange,
    onBrandOrganicChange,
    resetComposerValues,
  } = useTikTokComposerValues();

  const { authenticate, authenticating } = useTikTokAuthenticator({
    force: true,
    onAuthSuccess: async () => {
      await dispatch(getTikTokCreatorInfo(providerUserId));

      onAuthSuccess();
    },
    onError: error => {
      if (error.message === TITKOK_ACCESS_DENIED_MESSAGE) {
        goToStep('access-denied');
      } else {
        dispatch(showError('Error authenticating with TikTok'));

        goToStep('authorization');
      }

      onAuthError(error);
    },
  });

  const handleShare = useOnShare({
    shareNow: useCallback(
      () =>
        dispatch(
          shareTikTokVideo(
            privacyLevel,
            userAllowance,
            brandContent,
            brandOrganic,
            description,
          ),
        ),
      [
        brandContent,
        brandOrganic,
        description,
        dispatch,
        privacyLevel,
        userAllowance,
      ],
    ),
    schedule: useCallback(
      (scheduledDate: number) =>
        dispatch(
          scheduleTikTokVideo(
            privacyLevel,
            userAllowance,
            brandContent,
            brandOrganic,
            description,
            scheduledDate,
          ),
        ),
      [
        brandContent,
        brandOrganic,
        description,
        dispatch,
        privacyLevel,
        userAllowance,
      ],
    ),
  });

  const tiktokAuthorizationStep = useTikTokAuthorizationStep();

  const tiktokAccessDeniedStep = useSocialShareAccessDeniedStep({
    message:
      'We can’t post your video unless you allow us to manage and publish to your account.',
    onCancel: () => goToStep('composer'),
  });

  const tiktokPostComposerStep = useTikTokComposerStep({
    brandedContentSelected,
    privacyLevel,
    userAllowance,
    onPrivacyLevelChange,
    onUserAllowanceChange,
    onShare: handleShare,
  });

  const tiktokDisclosureStep = useTikTokDisclosureStep({
    isDisclosureVisible,
    brandContent,
    brandOrganic,
    onBrandContentChange,
    onBrandOrganicChange,
    onVisibilityChange,
    resetComposerValues,
  });

  return {
    platformAlias: 'tiktok',
    authenticating: authenticating || !username || !creatorInfo,
    accounts: [
      {
        id: providerUserId,
        avatarSrc: profileImageUrl,
        username,
        handle: username,
      },
    ],
    steps: [
      tiktokAuthorizationStep,
      tiktokAccessDeniedStep,
      tiktokPostComposerStep,
      tiktokDisclosureStep,
    ],
    defaultStep: tiktokPostComposerStep.id,
    postStatus,
    authenticate,
  };
}
